import {Outlet, useNavigate, useLocation, Link} from "react-router-dom";
import {useTranslation} from "react-i18next";
import Container from 'react-bootstrap/Container';
import Nav from 'react-bootstrap/Nav';
import Navbar from 'react-bootstrap/Navbar';
import ReactCountryFlag from "react-country-flag";
import {useCallback, useEffect, useState} from "react";
import LoginModal from "./common/LoginModal";
import {getLanguage, getLoggedUser, logout, setLanguage, validateToken} from "../util/SecurityUtils";
import {useIsMobile} from "../hooks/useIsMobile";
import { ThemeSwitcher } from "./common/ThemeSwitcher";
import { useTheme } from "../context/ThemeContext";
import { useEasterEggs } from "../hooks/useEasterEggs";
import { AnimatePresence, motion, type Transition } from "framer-motion";
import { useChatLatestTimestamp } from "../hooks/queries/useChatMessages";
import { useChatLastVisit } from "../hooks/useChatLastVisit";
import { useNewsLastVisit } from "../hooks/useNewsLastVisit";
import { LATEST_NEWS_TIMESTAMP_MS } from "../pages/Changelog";

const pageVariants = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0 }
};

const pageTransition: Transition = {
  type: 'tween',
  ease: 'easeOut',
  duration: 0.3
};

const Navigation = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { theme } = useTheme();
  const isMobile = useIsMobile();
  const [ showLoginModal, setShowLoginModal ] = useState(false)
  const [ loggedUser, setLoggeduser ] = useState<ReturnType<typeof getLoggedUser>>(null)
  const [expanded, setExpanded] = useState(false);
  const { handleLogoClick, konamiActivated, secretMode } = useEasterEggs();
  const { data: latestChat } = useChatLatestTimestamp();
  const chatLastVisit = useChatLastVisit();
  const onChatPage = location.pathname === "/chat";
  const hasUnreadChatMention = !!latestChat?.mentionTimestamp
    && !onChatPage
    && new Date(latestChat.mentionTimestamp).getTime() > chatLastVisit;
  const hasUnreadChat = !!latestChat?.timestamp
    && !onChatPage
    && new Date(latestChat.timestamp).getTime() > chatLastVisit;
  const newsLastVisit = useNewsLastVisit();
  const hasUnreadNews = location.pathname !== "/news"
    && LATEST_NEWS_TIMESTAMP_MS > newsLastVisit;
  const handleOnClick = useCallback((path: string) => {
    navigate(path)
    setExpanded(false);
  }, [navigate, setExpanded]);
  const closeNav = useCallback(() => setExpanded(false), [setExpanded]);
  // The ladders submenu is a hover dropdown, but its visibility is driven by JS state (not CSS
  // :hover) so that picking an item closes it immediately even while the cursor is still over the
  // menu. Pure :hover would keep it open until the mouse physically leaves.
  const [laddersOpen, setLaddersOpen] = useState(false);
  const closeLaddersMenu = useCallback(() => {
    closeNav();
    setLaddersOpen(false);
  }, [closeNav]);

  useEffect(() => {
    validateToken().then(() => {setLoggeduser(getLoggedUser())})
    const language = getLanguage()
    i18n.changeLanguage(language)
  }, [])

  const logOut = () => {
    logout()
    navigate(0)
  }

  const changeLanguage = (lang: string) => {
    setLanguage(lang)
    i18n.changeLanguage(lang);
  }

  return (
    <>
      <Navbar expand="lg" expanded={expanded} className={`theme-${theme}`} style={{ backgroundColor: 'var(--navbar-bg)' }}>
        <Container>
          <Navbar.Brand
            as={Link}
            to="/"
            onClick={() => {
              handleLogoClick();
              closeNav();
            }}
            style={{cursor: "pointer", color: 'var(--navbar-text)', textDecoration: 'none'}}
          >
            <img
              alt=""
              src="/icons/table-tennis.png"
              width="30"
              height="30"
              className="d-inline-block align-top"
              style={{
                filter: konamiActivated || secretMode ? 'hue-rotate(180deg)' : 'none',
                transition: 'filter 0.3s ease'
              }}
            />{' '}Hravé Turnaje
          </Navbar.Brand>
          <Navbar.Toggle aria-controls="basic-navbar-nav" onClick={() => setExpanded(!expanded)} />
          <Navbar.Collapse id="basic-navbar-nav">
            <Nav>
              <Nav.Link as={Link} to="/tournaments" onClick={closeNav}>{t("tournaments")}</Nav.Link>
              {isMobile ? (
                // On mobile the dropdown pattern doesn't work well — expose both sub-items
                // directly. Clicking "Žebříčky" itself also lands on the official page.
                <>
                  <Nav.Link as={Link} to="/players" onClick={closeNav}>{t("officialLadders")}</Nav.Link>
                  <Nav.Link as={Link} to="/ladders/unofficial" onClick={closeNav}>{t("unofficialLadders")}</Nav.Link>
                </>
              ) : (
                <div
                  className={"ladders-menu" + (laddersOpen ? " open" : "")}
                  onMouseEnter={() => setLaddersOpen(true)}
                  onMouseLeave={() => setLaddersOpen(false)}
                >
                  <Nav.Link
                    as={Link}
                    to="/players"
                    onClick={closeLaddersMenu}
                    className="ladders-menu-toggle"
                  >
                    {t("ladders")}
                    <span className="ladders-menu-caret" aria-hidden="true">▾</span>
                  </Nav.Link>
                  <div className="ladders-menu-submenu">
                    <Link to="/players" onClick={closeLaddersMenu} className="ladders-menu-item">
                      {t("officialLadders")}
                    </Link>
                    <Link to="/ladders/unofficial" onClick={closeLaddersMenu} className="ladders-menu-item">
                      {t("unofficialLadders")}
                    </Link>
                  </div>
                </div>
              )}
              <Nav.Link as={Link} to="/news" onClick={closeNav} className="nav-link-with-dot">
                {t("news")}
                {hasUnreadNews && <span className="nav-unread-dot" aria-label={t("unreadNews")} />}
              </Nav.Link>
              <Nav.Link as={Link} to="/hall-of-fame" onClick={closeNav}>{t("hallOfFame")}</Nav.Link>
              <Nav.Link as={Link} to="/chat" onClick={closeNav} className="nav-link-with-dot">
                {t("chat")}
                {hasUnreadChatMention
                  ? <span className="nav-unread-dot mention" aria-label={t("unreadChatMention")} />
                  : hasUnreadChat && <span className="nav-unread-dot" aria-label={t("unreadChatMessages")} />}
              </Nav.Link>
              <Nav.Link as={Link} to="/board" onClick={closeNav}>{t("board")}</Nav.Link>
              <Nav.Link as={Link} to="/about" onClick={closeNav}>{t("about")}</Nav.Link>
            </Nav>
          </Navbar.Collapse>
          <Navbar.Collapse className="justify-content-end">
            <Nav>
              { isMobile ? <hr className="my-2" style={{borderTop: "1px solid #dee2e6"}} /> : null }
              {loggedUser
                ? (loggedUser.playerId
                    ? <Nav.Link as={Link} to={`/players/${loggedUser.playerId}`} onClick={closeNav} className="me-3">{loggedUser.username}</Nav.Link>
                    : <Nav.Link className="me-3">{loggedUser.username}</Nav.Link>)
                : <Nav.Link onClick={() => setShowLoginModal(true)} className="me-3">{t("login")}</Nav.Link> }
              {loggedUser ? <Nav.Link onClick={logOut} className="me-3 gray-button">{t("logout")}</Nav.Link> : null }
              <div className="d-flex align-items-center">
                <Navbar.Text onClick={() => changeLanguage(i18n.language === "cs" ? "en" : "cs")} style={{cursor: "pointer"}}>
                  {i18n.language === "cs" ? <ReactCountryFlag countryCode="GB" svg title="GB" className="big-flag" />
                    : <ReactCountryFlag countryCode="CZ" svg title="CZ" className="big-flag" />
                  }
                  <a className="ms-1" style={{textDecoration: "none", color: 'var(--navbar-text)'}}>{i18n.language === "cs" ? "EN" : "CZ"}</a>
                </Navbar.Text>
                <ThemeSwitcher />
              </div>
            </Nav>
          </Navbar.Collapse>
        </Container>
      </Navbar>

      <AnimatePresence mode="wait">
        <motion.div
          key={location.pathname}
          initial="initial"
          animate="animate"
          exit="exit"
          variants={pageVariants}
          transition={pageTransition}
        >
          <Outlet />
        </motion.div>
      </AnimatePresence>
      <LoginModal isOpen={showLoginModal} closeModal={() => setShowLoginModal(false)} />
    </>
  );
};

export default Navigation;